<template lang="pug">
#reservation-list
  // title
  .d-flex.flex-row.justify-content-center.mb-1
    .page-title 面会予約一覧

  // filter
  .mb-2.d-flex.flex-row.align-items-center.flex-wrap(v-if="!isBusy")
    span.table-option-text 利用者グループ：
    .filter
      b-form-select(
        id="filter-input"
        v-model="filter"
        :options="filterOptions"
        size="md"
      )

  // reservation list
  ReservationTable(
    v-model="reservationItems"
    :fields="reservationFields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isBusy="isBusy"
    :filter="filter"
    :filterFields="filterFields"
    @onDetail="onDetail"
  )
  // pagenation
  .d-flex.flex-row.justify-content-center(v-if="!isBusy")
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isBusy"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import Setting from "../../../../public/setting.json";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import ReservationsService, { ReservationsGetModel } from "@/commons/services/Reservations";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import ReservationTable, { ReservationTableField, ReservationTableItem } from "@/mobile/components/tables/ReservationTable.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### 面会予約一覧コンポーネント
 */
@Component({
  components: {
    ReservationTable
  }
})
export default class ReservationList extends Vue {
  /** 面会予約サービス(複数) */
  @Inject("ReservationsService") reservationsService!: ReservationsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 読込み中か否か */
  isBusy: boolean = true;

  /** 面会予約列名 */
  reservationFields: Array<ReservationTableField> = [];
  /** 面会予約各行の情報 */
  reservationItems: Array<ReservationTableItem> = [];
  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["groupId"];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** メッセージ履歴1ページあたりの表示数 */
  perPage: number = Number(Setting.reservation.perPage);
  /** ページングの全体行数 */
  rows: number = 0;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.reservation.pageRange;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const reservationFields: Array<ReservationTableField> = this.createReservationFields();
    this.reservationFields = reservationFields;

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.reservationItems = [];
    this.currentRows = this.reservationItems.length;
    this.currentPage = 1;
    await this.createView();

    // ページ番号の復元は画面初回表示時でのみ行う
    // pageRowが設定されるまえにcurrentPageを復元してもライブラリがページ遷移をしてくれないので情報取得後にページ番号を復元する
    this.currentPage = this.$route.params.page ? Number(this.$route.params.page) : 1;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createReservationFields(): Array<ReservationTableField> {
    let fields: Array<ReservationTableField> = [];

    fields.push({ key: "reservation", label: "", sortable: false, sortDirection: "desc", class: "" });

    return fields;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name + "-mobile"] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name + "-mobile"];
    }
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // テーブルに渡す各プロパティを作成する
    const reservations: Array<ReservationsGetModel> = await this.getReservations();
    const reservationItems: Array<ReservationTableItem> = this.createReservationItems(reservations);
    this.reservationItems = this.reservationItems.concat(reservationItems);
    this.currentRows = this.reservationItems.length;

    this.isBusy = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      // 検索条件が指定されている場合は、その検索条件を使用し、無ければデフォルトを使用する
      let params: any = {};
      // 過去の面会予約は表示対象外にするため、開始日時を指定する
      const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000");
      params = {
        start: start
      };

      const count: number = await this.reservationsService.getCount(userStore, params);

      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 面会予約を取得する
   */
  public async getReservations(): Promise<Array<ReservationsGetModel>> {
    // ページ番号の復元がありえるので、ページ番号を復元する
    // 復元後かつページ遷移した場合、復元したcurrentPageよりもthis.currentPageが大きくなるのでその場合はthis.currentPageを使用する
    let currentPage: number = this.$route.params.page ? Number(this.$route.params.page) : 1;
    currentPage = this.currentPage < currentPage ? currentPage : this.currentPage;

    // 過去の面会予約は表示対象外にするため、開始日時を指定する
    const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000"); // 表示対象の利用者を取得する
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (currentPage + this.pageRange);
    const params: any = {
      start: start,
      startIndex: startIndex,
      endIndex: endIndex,
      sortKey: "datetime",
      order: "asc"
    };

    const response: Array<ReservationsGetModel> = await this.reservationsService.gets(userStore, params);

    return response;
  }

  /**
   * ### テーブルの行を作成する
   */
  public createReservationItems(reservations: Array<ReservationsGetModel>): Array<ReservationTableItem> {
    let items: Array<ReservationTableItem> = [];

    for (const reservation of reservations) {
      items.push({
        id: reservation.id,
        type: reservation.type,
        inputType: reservation.inputType,
        consented: reservation.consented ? reservation.consented : false,
        datetime: Number(reservation.datetime),
        resident: {
          name: reservation.residentName,
          room: reservation.residentRoom
        },
        family: {
          name: reservation.familyName,
          relation: reservation.familyRelation,
          id: reservation.familyId
        },
        tel: reservation.familyTel,
        status: reservation.consented ? reservation.consented : false,
        onlineVisitUrl: reservation.onlineVisitUrl ? reservation.onlineVisitUrl : "",
        updated: Number(reservation.updated),
        groupId: reservation.groupId
      });
    }

    return items;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 面会予約詳細画面へ遷移する
   */
  public onDetail(item: ReservationTableItem): void {
    this.$router.push({ name: MobileUrl.ReservationDetail, params: { id: String(item.id), page: String(this.currentPage) } });

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name + "-mobile", filter: this.filter });
    }
    next();
  }
}
</script>
